import { DocumentProviderType } from 'models/documentProvider';
import { DocumentOutputFolderRule, DocumentOutputPurpose } from 'models/documentOutputLocation';
import { TaskTypes } from 'models/tasks/task';

/** Whose settings a binding row holds. Mirrored by numeric value from the API. */
export enum OutputLocationScope {
  TenantDefault = 0,
  TaskTemplate = 1,
}

/** The default folder name format when the tenant row has none. */
export const defaultFolderNameFormat = '{TaskId} {TaskName}';

/** The default folder name format of the KPI attachments: the folder is the KPI's. */
export const defaultKpiFolderNameFormat = '{KpiId} {KpiName}';

/** The default folder name format of the Asset documents: the folder is the asset's (PLAN-073). */
export const defaultAssetFolderNameFormat = '{AssetId} {AssetName}';

export function getDefaultFolderNameFormat(purpose: DocumentOutputPurpose): string {
  switch (purpose) {
    case DocumentOutputPurpose.KpiAttachments:
      return defaultKpiFolderNameFormat;
    case DocumentOutputPurpose.AssetDocuments:
      return defaultAssetFolderNameFormat;
    default:
      return defaultFolderNameFormat;
  }
}

/**
 * The tokens the folder name field offers as chips, with the one date format it offers. The API's catalogue holds more
 * (the assignee, other date formats) and still validates and renders a saved format that uses them.
 */
export const offeredFolderNameTokens: { key: string; formats: string[] }[] = [
  { key: 'TaskId', formats: [] },
  { key: 'TaskName', formats: [] },
  { key: 'TemplateName', formats: [] },
  { key: 'Date', formats: ['yyyy-MM-dd'] },
];

/** The KPI attachments offer the KPI first; the task tokens are empty for a KPI answered outside a task. */
export const offeredKpiFolderNameTokens: { key: string; formats: string[] }[] = [
  { key: 'KpiId', formats: [] },
  { key: 'KpiName', formats: [] },
  { key: 'TaskId', formats: [] },
  { key: 'TaskName', formats: [] },
  { key: 'Date', formats: ['yyyy-MM-dd'] },
];

/** The Asset documents offer the asset's tokens only: the folder is made once per asset, not per task. */
export const offeredAssetFolderNameTokens: { key: string; formats: string[] }[] = [
  { key: 'AssetId', formats: [] },
  { key: 'AssetName', formats: [] },
  { key: 'AssetCode', formats: [] },
  { key: 'AssetType', formats: [] },
];

export function getOfferedFolderNameTokens(purpose: DocumentOutputPurpose): { key: string; formats: string[] }[] {
  switch (purpose) {
    case DocumentOutputPurpose.KpiAttachments:
      return offeredKpiFolderNameTokens;
    case DocumentOutputPurpose.AssetDocuments:
      return offeredAssetFolderNameTokens;
    default:
      return offeredFolderNameTokens;
  }
}

/** A token of the folder name format, as the API's catalogue lists it. */
export class FolderNameToken {
  key: string;

  /** The allowed formats, for the Date token only: '{Date:yyyy-Qq}'. */
  formats: string[];

  sample: string;

  constructor() {
    this.key = '';
    this.formats = [];
    this.sample = '';
  }
}

/** Which columns of a template row are empty and take the tenant row's value. */
export class OutputLocationBindingInheritance {
  list: boolean;

  folderRule: boolean;

  format: boolean;

  constructor() {
    this.list = true;
    this.folderRule = true;
    this.format = true;
  }
}

/** The values that apply after the inheritance from the tenant row. */
export class OutputLocationBindingEffective {
  listId?: number;

  /** The start folder that applies; undefined is the root of the effective category. */
  folderDriveItemId?: string;

  folderName?: string;

  folderRule: DocumentOutputFolderRule;

  folderNameFormat: string;

  constructor() {
    this.folderRule = DocumentOutputFolderRule.Flat;
    this.folderNameFormat = defaultFolderNameFormat;
  }
}

/**
 * The settings of one purpose for the tenant or for one task template. A template row's own listId, folderRule and
 * folderNameFormat are each undefined when the template takes the tenant row's value. Its own start folder lies in its
 * own category or, without one, in the tenant's; a template on the tenant's category without a folder takes the
 * tenant row's folder.
 */
export default class OutputLocationBinding {
  bindingId?: number;

  scope: OutputLocationScope;

  templateTaskId?: number;

  templateName?: string;

  templateTaskType?: TaskTypes;

  listId?: number;

  listName?: string;

  listUrl?: string;

  providerType?: DocumentProviderType;

  folderDriveItemId?: string;

  folderName?: string;

  folderRule?: DocumentOutputFolderRule;

  folderNameFormat?: string;

  inherited: OutputLocationBindingInheritance;

  effective: OutputLocationBindingEffective;

  constructor() {
    this.scope = OutputLocationScope.TenantDefault;
    this.inherited = new OutputLocationBindingInheritance();
    this.effective = new OutputLocationBindingEffective();
  }

  isEqual(other: OutputLocationBinding): boolean {
    return (
      this.listId === other.listId &&
      this.folderDriveItemId === other.folderDriveItemId &&
      this.folderRule === other.folderRule &&
      this.folderNameFormat === other.folderNameFormat
    );
  }

  clone(): OutputLocationBinding {
    const output = new OutputLocationBinding();
    output.bindingId = this.bindingId;
    output.scope = this.scope;
    output.templateTaskId = this.templateTaskId;
    output.templateName = this.templateName;
    output.templateTaskType = this.templateTaskType;
    output.listId = this.listId;
    output.listName = this.listName;
    output.listUrl = this.listUrl;
    output.providerType = this.providerType;
    output.folderDriveItemId = this.folderDriveItemId;
    output.folderName = this.folderName;
    output.folderRule = this.folderRule;
    output.folderNameFormat = this.folderNameFormat;
    output.inherited = { ...this.inherited };
    output.effective = { ...this.effective };

    return output;
  }
}

/** The bindings of one purpose: the tenant row, the template rows and the token catalogue of the format. */
export class OutputLocationBindings {
  default: OutputLocationBinding;

  templates: OutputLocationBinding[];

  tokens: FolderNameToken[];

  constructor() {
    this.default = new OutputLocationBinding();
    this.templates = [];
    this.tokens = [];
  }

  clone(): OutputLocationBindings {
    const output = new OutputLocationBindings();
    output.default = this.default.clone();
    output.templates = this.templates.map((binding) => binding.clone());
    output.tokens = this.tokens.map((token) => ({ ...token, formats: [...token.formats] }));

    return output;
  }
}

/** A format rendered on a sample or a real task: one or two folder levels, or the problems of the format. */
export class FolderNamePreview {
  levels: string[];

  problems: string[];

  constructor() {
    this.levels = [];
    this.problems = [];
  }
}
